import React, { useEffect, useRef } from "react";
import cn from "classnames";
import gsap from "gsap";
import { useScroll } from "@ottomotors/ottomotors.com/hooks";
import {
  LayoutProvider,
  UniversalLinkWrapper,
} from "@ottomotors/ottomotors-common/components";
import { useBreakpoints } from "@ottomotors/ottomotors-common/hooks";
import { getCroppedImageUrl } from "@ottomotors/ottomotors-common/utils";
import { SliceConfig, TextLockup } from "@ottomotors/ottomotors.com/components";

import * as styles from "./styles.module.scss";

// todo: Props

const HeroTextAndMedia = ({
  index,
  data: {
    sliceConfig,
    media,
    textLockup,
    textAlignment = "left",
    mediaAlignment = "center",
    mediaOverlay,
    textBackground,
    showClientLogos,
    clientLogosTextLockup,
    clientLogosClients,
  },
  inView, // available to every slice
}) => {
  const { scrollY } = useScroll();
  const { giant } = useBreakpoints();

  const mediaParallaxRef = useRef();
  const textParallaxRef = useRef();

  const hasClientLogos = showClientLogos && clientLogosClients?.length > 0;
  const hasClientLogosText = hasClientLogos && clientLogosTextLockup;

  // always set textlockup theme to 'light' if textBackground is set
  const textLockUpTheme = textBackground ? "light" : textLockup?.theme?.theme;

  const textLockupConfig = {
    ...textLockup,
    theme: {
      theme: textLockUpTheme,
    },
  };

  const ConditionalLinkWrapper = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (scrollY > window.innerHeight) {
      return;
    }

    if (!mediaParallaxRef?.current || !textParallaxRef?.current) {
      return;
    }

    // todo: tweak transform % to in/decrease the effect
    const mediaScrollSeverity = 400; // lower means more movement
    const mediaScrollTransform = scrollY / mediaScrollSeverity;

    const mediaScaleSeverity = 10000; // lower means more movement
    const mediaScaleTransform = scrollY / mediaScaleSeverity; // lower means more movement

    //
    // media parallax

    const media = mediaParallaxRef.current;

    gsap.killTweensOf(media);

    gsap.to(media, {
      y: `${-5 + mediaScrollTransform}%`,
      // scale: 1 + mediaScaleTransform,
      duration: 0,
      ease: "none",
    });

    //
    // media parallax

    const text = textParallaxRef.current;

    const textSeverity = 20; // lower means more movement
    const textScrollTransform = scrollY / textSeverity;

    gsap.killTweensOf(text);

    gsap.to(text, {
      y: `${textScrollTransform}%`,
      duration: 0.125,
      ease: "power4.out",
    });
  }, [mediaParallaxRef, scrollY, textParallaxRef]);

  const renderMarquee = (keyPrefix) => (
    <div className={styles.marquee}>
      {clientLogosClients.map((client) => {
        const { image, link } = client || {};

        const imgUrl = getCroppedImageUrl(image?.asset?.url, image?.crop);
        const hotspotStyle = image?.hotspot
          ? {
              objectPosition: `${image.hotspot.x * 100}% ${image.hotspot.y * 100}%`,
            }
          : undefined;

        return (
          <figure
            key={`${keyPrefix}-${client?._key}`}
            className={styles.logo}
          >
            <ConditionalLinkWrapper
              condition={link?.[0]}
              wrapper={(children) => (
                <UniversalLinkWrapper data={{ link: link }}>
                  {children}
                </UniversalLinkWrapper>
              )}
            >
              <img
                src={imgUrl}
                alt={image?.altText || "OTTO Motors"}
                style={hotspotStyle}
              />
            </ConditionalLinkWrapper>
          </figure>
        );
      })}
    </div>
  );

  const logoBgStyle = {
    backgroundColor: sliceConfig?.backgroundColor?.hex || "#EFEFEF",
  };

  return (
    <SliceConfig config={sliceConfig}>
      <section className={styles.container}>
        <div className={cn(styles.viewport, { [styles.viewportNoLogos]: !hasClientLogos })}>
          <div className={styles.heroContent}>
            <LayoutProvider className={cn(styles.grid, { [styles.gridWithLogosText]: hasClientLogosText })} grid>
              <TextLockup
                forwardRef={textParallaxRef}
                className={cn(styles.text, {
                  [styles.alignRight]: textAlignment === "right",
                  [styles.paddedBackground]: textBackground,
                  [styles.paddedBackgroundLight]: textBackground === "light",
                  [styles.paddedBackgroundDark]: textBackground === "dark",
                })}
                data={textLockupConfig}
                hasData={textLockup}
                inView={inView}
              />
            </LayoutProvider>

            <div className={styles.background}>
              <figure
                ref={mediaParallaxRef}
                className={cn(styles.figure, {
                  [styles.alignLeft]: mediaAlignment === "left",
                  [styles.alignRight]: mediaAlignment === "right",
                  [styles.alignCenter]:
                    !mediaAlignment || mediaAlignment === "center",
                })}
              >
                {(media?.video?.url && (
                  <video autoPlay muted loop playsInline>
                    <source src={media.video.url} type="video/mp4" />
                  </video>
                )) || (
                  <>
                    {media?.image?.asset && (
                      <img src={media.image.asset.url} alt={media.image.alt} />
                    )}
                  </>
                )}

                {mediaOverlay && <div className={styles.backgroundOverlay} />}
              </figure>
            </div>
          </div>

          {hasClientLogos && !hasClientLogosText && (
            <div className={styles.clientLogos} style={logoBgStyle}>
              <div className={styles.clientLogosMarquee}>
                <div className={styles.logos}>
                  {renderMarquee("hero-logo")}
                  {renderMarquee("hero-logo-dup")}
                  {giant && renderMarquee("hero-logo-giant")}
                </div>
              </div>
            </div>
          )}
        </div>

        {hasClientLogos && hasClientLogosText && (
          <div className={cn(styles.clientLogos, styles.clientLogosWithText)} style={logoBgStyle}>
            <div className={styles.clientLogosText}>
              <LayoutProvider grid>
                <div className={styles.clientLogosTextLockup}>
                  <TextLockup data={clientLogosTextLockup} />
                </div>
              </LayoutProvider>
            </div>

            <div className={styles.clientLogosMarquee}>
              <div className={styles.logos}>
                {renderMarquee("hero-logo")}
                {renderMarquee("hero-logo-dup")}
                {giant && renderMarquee("hero-logo-giant")}
              </div>
            </div>
          </div>
        )}
      </section>
    </SliceConfig>
  );
};

export default HeroTextAndMedia;
